import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  Spinner,
  Textarea,
  TextareaOnChangeData,
} from "@fluentui/react-components";
import { CommentAdd16Filled, CommentAdd16Regular } from "@fluentui/react-icons";
import { ConstantValues } from "common/constants";
import { ChangeEvent, useState } from "react";
import { IAddCommentDialogProps } from "./AddCommentDialog.types";

export const AddCommentDialog = (props: IAddCommentDialogProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [value, setValue] = useState<string>(ConstantValues.EMPTY_STRING);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [hovered, setHovered] = useState<"comment" | null>(null);

  const MAX_LENGTH: number = 250;

  const handleSubmit = async () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setErrorMessage("Comment is required.");
      return;
    }

    if (trimmed.length > MAX_LENGTH) {
      setErrorMessage(`Comment must be less than ${MAX_LENGTH} characters.`);
      return;
    }

    setIsLoading(true);
    setErrorMessage(ConstantValues.EMPTY_STRING);

    try {
      await props.onSubmit(props.messageId, trimmed);
      setValue(ConstantValues.EMPTY_STRING);
      setOpen(false);
    } catch (err) {
      console.error("Error submitting comment", err);
      setErrorMessage("Failed to submit comment.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(_, { open }) => setOpen(open)}
      modalType="modal"
    >
      <DialogTrigger disableButtonEnhancement>
        <Button
          size="small"
          appearance="subtle"
          icon={
            hovered === "comment" ? (
              <CommentAdd16Filled />
            ) : (
              <CommentAdd16Regular />
            )
          }
          title="Add Comment"
          onMouseEnter={() => setHovered("comment")}
          onMouseLeave={() => setHovered(null)}
        />
      </DialogTrigger>

      <DialogSurface>
        <DialogBody>
          <DialogTitle>Add a comment</DialogTitle>

          <DialogContent style={{ width: "100%" }}>
            {isLoading ? (
              <Spinner label="Submitting comment..." />
            ) : (
              <>
                <Field
                  label="Comment"
                  required
                  style={{ width: "100%" }}
                  validationMessage={errorMessage || undefined}
                  validationState={errorMessage ? "error" : undefined}
                >
                  <Textarea
                    value={value}
                    maxLength={MAX_LENGTH}
                    onChange={(
                      _: ChangeEvent<HTMLTextAreaElement>,
                      data: TextareaOnChangeData,
                    ) => {
                      setValue(data.value);
                      if (errorMessage)
                        setErrorMessage(ConstantValues.EMPTY_STRING);
                    }}
                    style={{ width: "100%" }}
                    resize="vertical"
                  />
                </Field>
                <div
                  style={{ fontSize: 12, color: "#666", textAlign: "right" }}
                >
                  {value.trim().length}/{MAX_LENGTH}
                </div>
              </>
            )}
          </DialogContent>

          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" onClick={() => setOpen(false)}>
                Cancel
              </Button>
            </DialogTrigger>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              disabled={isLoading || !value.trim()}
            >
              Submit
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
